import { FaFilePdf } from "react-icons/fa6";

import { FONT_STACKS } from "../domain/canvas-model";

/**
 * Redesigned PDF content-module card (shared editor + public Grid). A compact
 * PORTRAIT document card: optional preview photo as background (with a dark
 * gradient for legibility), a red PDF badge top-left, and the title + an OPEN
 * affordance anchored to the bottom. Rendered at a fixed natural PORTRAIT size;
 * the caller wraps it in <FitToBox> so it scales to the slot. On the Grid the
 * parent wraps it in an <a> that opens the real document.
 */
export function PdfCard({ title, scale, interactive, bgImageUrl }: { title?: string; scale: number; interactive: boolean; bgImageUrl?: string }) {
  const px = (n: number) => Math.max(0, n * scale);
  const natW = Math.max(40, px(168));
  const natH = Math.max(56, px(224));
  const pad = Math.max(6, px(12));
  const badgeFont = Math.max(6, px(11));
  const titleFont = Math.max(8, px(16));
  const metaFont = Math.max(5, px(9));
  const displayTitle = title && title.trim() ? title.trim() : "Document";
  const hasBg = Boolean(bgImageUrl);
  return (
    <div
      className={interactive ? "pdf-card pdf-card--hover" : "pdf-card"}
      style={{
        position: "relative", width: natW, height: natH, boxSizing: "border-box",
        background: hasBg ? "#0b0f16" : "linear-gradient(160deg, #161e2e 0%, #0d121c 100%)",
        border: `${Math.max(1, px(1))}px solid rgba(239,68,68,0.40)`,
        borderRadius: px(12),
        boxShadow: "0 6px 22px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.05)",
        overflow: "hidden",
      }}
    >
      {/* optional preview background */}
      {hasBg && (
        <img src={bgImageUrl} alt="" draggable={false}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
      )}
      {/* legibility scrim (stronger at the bottom where the text sits) */}
      <div style={{ position: "absolute", inset: 0,
        background: hasBg
          ? "linear-gradient(180deg, rgba(6,8,13,0.35) 0%, rgba(6,8,13,0.15) 40%, rgba(6,8,13,0.85) 100%)"
          : "transparent" }} />

      {/* content */}
      <div style={{ position: "relative", width: "100%", height: "100%", boxSizing: "border-box", padding: pad, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        <span style={{
          alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: px(4),
          background: "#ef4444", color: "#fff", fontFamily: FONT_STACKS.GRID_MONO, fontWeight: 700,
          borderRadius: px(4), padding: `${px(3)}px ${px(7)}px`, fontSize: badgeFont, lineHeight: 1,
          letterSpacing: "0.06em",
        }}>
          <FaFilePdf size={badgeFont * 1.15} color="#fff" />
          PDF
        </span>

        <div style={{ display: "flex", flexDirection: "column", gap: px(8) }}>
          <span style={{ fontFamily: FONT_STACKS.GRID_SANS, fontWeight: 700, fontSize: titleFont, color: "#f2f5fa",
            lineHeight: 1.15, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
            textShadow: hasBg ? "0 1px 3px rgba(0,0,0,0.6)" : "none" }}>
            {displayTitle}
          </span>
          <span style={{
            alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: px(4),
            background: "rgba(239,68,68,0.16)", border: `${Math.max(1, px(1))}px solid rgba(239,68,68,0.35)`,
            color: "#fca5a5", borderRadius: px(4), fontSize: metaFont, fontFamily: FONT_STACKS.GRID_MONO,
            padding: `${px(4)}px ${px(9)}px`, textTransform: "uppercase", letterSpacing: "0.12em", whiteSpace: "nowrap",
          }}>
            Open document ↗
          </span>
        </div>
      </div>
    </div>
  );
}
